import { useEffect, useState } from "react";
import { CalendarDays, Pencil, Plus, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import MiniCalendar from "@/components/MiniCalendar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", unit_type: "truck", capacity: 100, equipment: "", status: "active" };
const MAX_CAPACITY = 300;

export default function Fleet() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [calUnit, setCalUnit] = useState(null);
  const [bookedDates, setBookedDates] = useState([]);
  const today = new Date();
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());

  const load = () => api.get("/units").then((r) => setItems(r.data.filter((u) => !u.archived))).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, unit_type: u.unit_type, capacity: u.capacity, equipment: (u.equipment || []).join(", "), status: u.status });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, capacity: Number(form.capacity), equipment: form.equipment.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editing) await api.patch(`/units/${editing.id}`, payload);
      else await api.post("/units", payload);
      toast.success(editing ? "Unit updated" : "Unit added to fleet");
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name} permanently?`)) return;
    try {
      await api.delete(`/units/${u.id}`);
      toast.success("Unit deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openCalendar = async (u) => {
    setCalUnit(u);
    try {
      const { data } = await api.get(`/units/${u.id}/booked-dates`);
      setBookedDates(data);
      if (data.length) {
        const [y, m] = data[0].event_date.split("-").map(Number);
        setCalYear(y);
        setCalMonth(m - 1);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setStatus = async (u, status) => {
    try {
      await api.patch(`/units/${u.id}`, { status });
      toast.success(`${u.name} → ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const navigateMonth = (delta) => {
    const d = new Date(calYear, calMonth + delta, 1);
    setCalYear(d.getFullYear());
    setCalMonth(d.getMonth());
  };
  const bookedMap = Object.fromEntries(bookedDates.map((b) => [b.event_date, b]));

  return (
    <div data-testid="fleet-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Fleet</h1>
          <p className="mt-1 text-sm text-muted-foreground">Your own catering vans and units — booked dates are auto-blocked per unit.</p>
        </div>
        <Button data-testid="new-unit-button" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="rounded-md bg-ink hover:bg-ink-light">
          <Plus className="mr-2 h-4 w-4" /> Add Unit
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.map((u) => (
          <div key={u.id} data-testid={`unit-card-${u.id}`} className="border border-border bg-white p-5 transition-transform duration-200 hover:-translate-y-[2px]">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center bg-muted"><Truck className="h-5 w-5 text-ink" /></div>
                <div>
                  <h3 className="font-heading text-base font-bold tracking-tight">{u.name}</h3>
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{u.unit_type}</p>
                </div>
              </div>
              <StatusBadge status={u.status} />
            </div>

            <div className="mt-4">
              <div className="mb-1 flex items-center justify-between text-xs">
                <span className="font-semibold uppercase tracking-[0.15em] text-muted-foreground">Throughput</span>
                <span className="font-bold tabular-nums" data-testid={`unit-capacity-${u.id}`}>{u.capacity} covers</span>
              </div>
              <div className="h-1.5 w-full bg-muted"><div className="h-full bg-ink" style={{ width: `${Math.min(100, (u.capacity / MAX_CAPACITY) * 100)}%` }} /></div>
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {(u.equipment || []).map((eq) => <span key={eq} className="bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">{eq}</span>)}
            </div>

            <div className="mt-4 border-t border-border pt-4">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Operational status</Label>
              <Select value={u.status} onValueChange={(v) => setStatus(u, v)}>
                <SelectTrigger data-testid={`unit-status-select-${u.id}`} className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="deployed">Deployed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="mt-3 flex gap-2">
              <Button data-testid={`unit-calendar-${u.id}`} size="sm" variant="outline" className="rounded-md" onClick={() => openCalendar(u)}><CalendarDays className="mr-1 h-3.5 w-3.5" /> Diary</Button>
              <Button data-testid={`edit-unit-${u.id}`} size="sm" variant="outline" className="rounded-md" onClick={() => openEdit(u)}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
              <Button data-testid={`delete-unit-${u.id}`} size="sm" variant="outline" className="rounded-md border-red-300 text-red-600 hover:bg-red-50" onClick={() => remove(u)}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="col-span-3 border border-border bg-white px-5 py-10 text-center text-sm text-muted-foreground">No units in the fleet yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="unit-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Unit" : "Add Mobile Unit"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5"><Label>Unit name</Label><Input data-testid="unit-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={form.unit_type} onValueChange={(v) => setForm({ ...form, unit_type: v })}>
                  <SelectTrigger data-testid="unit-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="truck">Food Truck</SelectItem>
                    <SelectItem value="trailer">Trailer</SelectItem>
                    <SelectItem value="kitchen">Mobile Kitchen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Capacity (covers)</Label><Input data-testid="unit-capacity-input" type="number" min="1" required value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Equipment (comma separated)</Label><Input data-testid="unit-equipment-input" value={form.equipment} onChange={(e) => setForm({ ...form, equipment: e.target.value })} /></div>
            <Button data-testid="unit-submit-button" type="submit" className="w-full rounded-md bg-ink hover:bg-ink-light">{editing ? "Save Changes" : "Add Unit"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!calUnit} onOpenChange={() => setCalUnit(null)}>
        <DialogContent className="max-w-2xl bg-white" data-testid="unit-calendar-dialog">
          {calUnit && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{calUnit.name} — diary</DialogTitle></DialogHeader>
              <MiniCalendar year={calYear} month={calMonth} onNavigate={navigateMonth} testid="unit-diary-calendar"
                dayContent={(ds) => bookedMap[ds] && (
                  <div className="bg-red-100 px-1.5 py-0.5 text-[11px] font-semibold text-red-800" data-testid={`unit-booked-${ds}`}>
                    Booked · {bookedMap[ds].booking_ref}
                  </div>
                )}
              />
              <p className="text-xs text-muted-foreground">Dates in red are confirmed bookings for this unit and can't be double-booked.</p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
